/**
 * Spec 031 A1: the PCs that run agents for the tasker, as a person reads them — whether a PC is
 * there, what a command did, fact by fact, and what may still be done with it. Never «работает»
 * on a guess: a process is «жив» only with the time the PC confirmed it.
 */

import type { AgentActiveRun, DeviceCommandOut, DeviceCommandState, DeviceOut } from "@/api/types";
import { sinceWords } from "@/lib/agents";

/** «на связи», «нет связи 5 минут», «отключён». */
export function deviceLine(d: Pick<DeviceOut, "online" | "last_seen_at" | "revoked_at">, now: number = Date.now()): {
  text: string;
  tone: "done" | "waiting" | "blocked";
} {
  if (d.revoked_at) return { text: "отключён", tone: "blocked" };
  if (d.online) return { text: "на связи", tone: "done" };
  if (!d.last_seen_at) return { text: "ещё не выходил на связь", tone: "waiting" };
  const since = sinceWords(d.last_seen_at, now);
  return { text: since === "только что" ? "нет связи меньше минуты" : `нет связи ${since.replace(/ назад$/, "")}`, tone: "waiting" };
}

/** «Процесс на ПК: жив, проверено 2 минуты назад» — or why it is not known. Null: not a PC's run. */
export function processLine(
  run: Pick<AgentActiveRun, "device_name" | "process" | "process_checked_at">,
  now: number = Date.now(),
): string | null {
  if (!run.device_name || !run.process) return null;
  const checked = run.process_checked_at ? sinceWords(run.process_checked_at, now) : null;
  if (run.process === "alive") return `Процесс на ПК ${run.device_name}: жив, проверено ${checked ?? "только что"}`;
  if (run.process === "not_found") return `Процесс на ПК ${run.device_name}: не найден${checked ? ` (проверено ${checked})` : ""}`;
  return checked
    ? `Процесс на ПК ${run.device_name}: неизвестно, последний раз подтверждён ${checked}`
    : `Процесс на ПК ${run.device_name}: неизвестно — ПК не подтверждал`;
}

const WAITING: DeviceCommandState[] = ["queued", "delivered"];
const ON_PC: DeviceCommandState[] = ["accepted", "starting", "started", "unknown"];

/** What may still be done with a launch command (A1 К7, К8). */
export function commandActions(c: Pick<DeviceCommandOut, "kind" | "state">): {
  cancel: boolean;
  stop: boolean;
  resolve: boolean;
} {
  const launch = c.kind === "launch";
  return {
    cancel: launch && WAITING.includes(c.state),
    stop: launch && ON_PC.includes(c.state),
    resolve: launch && c.state === "unknown",
  };
}

/** The command is still on its way or its process may live: keep looking at it. */
export function commandLive(c: Pick<DeviceCommandOut, "state">): boolean {
  return WAITING.includes(c.state) || ON_PC.includes(c.state);
}

/** The launch that holds the agent — or its sub-agent's own chat — on my PCs: what the server's
 * reservation is (A1 К2). `rows` newest first, as the server gives them. */
export function heldCommand(rows: DeviceCommandOut[], subagent: string | null): DeviceCommandOut | null {
  return rows.find((c) => c.kind === "launch" && (c.subagent ?? null) === (subagent ?? null) && commandLive(c)) ?? null;
}

/** The stops given to a launch, oldest first: they show under it. */
export function stopsOf(rows: DeviceCommandOut[], launchId: string): DeviceCommandOut[] {
  return rows.filter((c) => c.kind === "stop" && c.target_id === launchId).reverse();
}

/** The launches still to look at on the agent's card: one on its way or whose process may live,
 * or one whose stop is not done yet (review 7f478c78, 7). Oldest first. */
export function launchesOnPcs(rows: DeviceCommandOut[]): DeviceCommandOut[] {
  return rows
    .filter((c) => c.kind === "launch" && (commandLive(c) || stopsOf(rows, c.id).some(commandLive)))
    .reverse();
}

/** What a takeover will do with what holds the agent now — no more than is known
 * (review 7f478c78, 7). Null: nothing to take over. */
export function takeoverNote(
  held: Pick<DeviceCommandOut, "state" | "device_name"> | null,
  live: Pick<AgentActiveRun, "device_name"> | null,
): string | null {
  if (held?.state === "unknown") {
    return `Что с прежним процессом на ПК «${held.device_name}» — неизвестно: ПК не подтвердил ни запуск, ни выход. Перехват снимет резерв и пошлёт тому ПК остановку. Если процесс жив, он останется на ПК, пока тот не выйдет на связь и не закроет его; начать работу в таскере по старому запуску он уже не сможет.`;
  }
  if (!live) return null;
  if (live.device_name) {
    return `Перехват пошлёт ПК «${live.device_name}» остановку прежнего процесса. Пока ПК её не подтвердил, процесс может работать; его чат остановится, когда начнётся новый.`;
  }
  return "Прежний чат остановится, когда начнётся новый, — узнает об этом при следующем вызове. Сам процесс на том компьютере не закроется: его закрывают там.";
}

/** The PCs a person may launch on: their own, connected (revoked ones are history). */
export function launchTargets(devices: DeviceOut[]): DeviceOut[] {
  return devices.filter((d) => d.mine && !d.revoked_at);
}

/** «1234-5678» from what a person typed: digits only, a dash after four. Null until there are 8. */
export function pairCode(typed: string): string | null {
  const digits = typed.replace(/\D/g, "");
  return digits.length === 8 ? `${digits.slice(0, 4)}-${digits.slice(4)}` : null;
}
